/**
 * Экран «Организатор» (задача 24) — раздел владельца сервиса: четыре **вычисляемые**
 * таблицы по подтверждённым заявкам — «Подтверждённые заявки», «Приезды», «Отъезды» и
 * «Футболки».
 *
 * Экран ничего не редактирует и не принимает данных: строки считает сервер
 * (src/backend/organizer/organizerService.ts) и отдаёт их одним запросом
 * (`GET /api/organizer/screen`) — иначе экрану пришлось бы читать заявки всех команд,
 * а заявка каждого тренера закрыта его сессией. Сетевую часть показа (память последнего
 * экрана и условный запрос по `ETag`) ведёт `organizerCache.ts` (задача 35, этап D).
 *
 * Оформление то же, что у таблиц экрана заявки: сворачиваемые секции (`sectionFrame`),
 * карточная раскладка на узком экране и настоящая таблица на широком (стили .table).
 * Права проверяет API (`withRoles`), а адрес `#/organizer` показывается только роли
 * организатора (`main.ts`).
 */
import { stationName, TSHIRT_SIZES } from '../shared/dictionaries';
import type {
  OrganizerApplicationRow,
  OrganizerApplicationTotals,
  OrganizerScreen,
  OrganizerTransferRow,
  OrganizerTshirtRow,
  OrganizerTshirtTotals,
  PublicUser,
} from '../shared/types';
import { userMasthead } from './accountMenu';
import { el, link, messageBox, renderScreen } from './dom';
import { cachedOrganizerScreen, refreshOrganizerScreen } from './organizerCache';
import { organizerTeamHref } from './router';
import {
  cell,
  emptyNote,
  readonlyTable,
  sectionFrame,
  showLoadError,
  valueCell,
} from './ui';

/**
 * Подписи колонок таблицы «Подтверждённые заявки». Слово «Количество» из подписей убрано
 * (правка организатора 20.10.2026): в таблице колонка и без него читается как счёт.
 */
const APPLICATION_COLUMNS = [
  'Название команды',
  'Город',
  'Руководитель',
  'Телефон',
  'Спортсмены',
  'Представители',
  'Выступления',
];

/**
 * Подписи колонок таблицы «Приезды» (с колонкой «Раннее заселение»). Порядок колонок
 * повторяет порядок полей строки приезда: дата, время, количество человек, вокзал,
 * «Трансфер нужен», номер поезда или рейса (тот же порядок в форме заявки). Подпись
 * колонки с числом людей сокращена до «Человек» — без слова «Количество»
 * (правка организатора 20.10.2026).
 */
const ARRIVAL_COLUMNS = [
  'Название команды',
  'Город',
  'Дата приезда',
  'Время приезда',
  'Человек',
  'Вокзал / аэропорт',
  'Трансфер нужен',
  'Номер поезда / рейса',
  'Раннее заселение',
];

/** Подписи колонок таблицы «Отъезды»: колонки «Раннее заселение» у отъездов нет. */
const DEPARTURE_COLUMNS = [
  'Название команды',
  'Город',
  'Дата отъезда',
  'Время отъезда',
  'Человек',
  'Вокзал / аэропорт',
  'Трансфер нужен',
  'Номер поезда / рейса',
];

/** Подпись последней колонки таблицы «Футболки» — общее количество на команду. */
const TSHIRT_TOTAL_COLUMN = 'Всего';

/** Подпись подвала таблиц «Приезды» и «Отъезды» — общее число человек по таблице. */
const PEOPLE_TOTAL_LABEL = 'Всего человек';

/**
 * Подпись первой ячейки итоговой строки таблицы «Подтверждённые заявки»: команда — это
 * строка таблицы, а не значение колонки, поэтому число команд стоит в ячейке, которая
 * занимает колонки команды (название, город, руководитель, телефон).
 */
const TEAM_TOTAL_LABEL = 'Всего команд';

/** Текст пустой секции: подтверждённых заявок нет — таблице нечего показывать. */
const NO_APPLICATIONS = 'Подтверждённых заявок пока нет.';

/**
 * Открывает экран «Организатор»: рисует четыре секции — сразу из памяти браузера, если
 * раздел уже показывали, затем по свежему ответу сервера (задача 35, этап D).
 */
export function showOrganizerScreen(user: PublicUser): void {
  const message = messageBox();
  const sections = el('div', { className: 'application' });
  renderScreen(
    'Организатор',
    el('div', { className: 'application-screen', children: [message.root, sections] }),
    userMasthead(user),
  );

  /** Рисует четыре секции: вызывается и для сохранённого экрана, и для свежего. */
  const draw = (screen: OrganizerScreen): void => {
    message.clear();
    sections.replaceChildren(
      applicationsSection(screen.applications, screen.applicationTotals),
      transfersSection({
        title: 'Приезды',
        columns: ARRIVAL_COLUMNS,
        rows: screen.arrivals,
        peopleTotal: screen.arrivalPeopleTotal,
        withEarlyCheckIn: true,
      }),
      transfersSection({
        title: 'Отъезды',
        columns: DEPARTURE_COLUMNS,
        rows: screen.departures,
        peopleTotal: screen.departurePeopleTotal,
        withEarlyCheckIn: false,
      }),
      tshirtsSection(screen.tshirts, screen.tshirtTotals),
    );
  };

  // Сохранённого экрана нет (первое открытие раздела или другая сессия) — рисовать пока
  // нечего, поэтому показываем сообщение о загрузке. С сохранённым экраном раздел
  // открывается мгновенно, а таблицы перерисуются только при изменённых данных.
  if (!cachedOrganizerScreen()) message.show('Загружаем раздел…', 'info');

  refreshOrganizerScreen(draw).catch((err: unknown) => showLoadError(err, message));
}

/** Секция экрана: тот же сворачиваемый каркас, что у секций заявки. */
function section(title: string, content: Node): HTMLElement {
  const body = el('div', { className: 'section__body', children: [content] });
  return sectionFrame(title, body).root;
}

/**
 * Название команды в таблице раздела — гиперссылка на «Заявку команды» (задача 43):
 * организатор попадает в заявку из любой строки, где команда упомянута, а все ссылки
 * ведут на один экран. Адрес команды (`ownerUserId`) приходит вместе со строкой.
 */
function teamLink(row: { ownerUserId: string; organizationName: string }): HTMLElement {
  return link(row.organizationName, organizerTeamHref(row.ownerUserId));
}


/**
 * Секция «Подтверждённые заявки»: команды, их состав и количество выступлений, а внизу —
 * итоговая строка «Всего команд» со счётом по всем командам.
 */
function applicationsSection(
  rows: OrganizerApplicationRow[],
  totals: OrganizerApplicationTotals,
): HTMLElement {
  const content =
    rows.length === 0
      ? emptyNote(NO_APPLICATIONS)
      : readonlyTable({
          columns: APPLICATION_COLUMNS,
          rows: rows.map((row) => [
            cell(APPLICATION_COLUMNS[0], teamLink(row)),
            cell(APPLICATION_COLUMNS[1], valueCell(row.city)),
            cell(APPLICATION_COLUMNS[2], valueCell(row.headName)),
            cell(APPLICATION_COLUMNS[3], valueCell(row.phone)),
            cell(APPLICATION_COLUMNS[4], valueCell(row.athleteCount)),
            cell(APPLICATION_COLUMNS[5], valueCell(row.representativeCount)),
            cell(APPLICATION_COLUMNS[6], valueCell(row.performanceCount)),
          ]),
          footer: applicationsFooter(totals),
        });
  return section('Подтверждённые заявки', content);
}

/**
 * Подвал таблицы «Подтверждённые заявки»: итоги по всем командам таблицы. Первая ячейка
 * занимает четыре колонки команды (название, город, руководитель, телефон): команд — число
 * строк таблицы, а не значение колонки, и подпись `colspan`-ячейки читается целиком и в
 * карточке узкого экрана. Итоги спортсменов, представителей и выступлений стоят под своими
 * колонками — как размеры в строке «Всего» таблицы «Футболки».
 */
function applicationsFooter(totals: OrganizerApplicationTotals): HTMLTableSectionElement {
  return el('tfoot', {
    children: [
      el('tr', {
        children: [
          el('td', {
            attrs: { colspan: 4 },
            text: `${TEAM_TOTAL_LABEL}: ${totals.teamCount}`,
          }),
          cell(APPLICATION_COLUMNS[4], valueCell(totals.athleteCount)),
          cell(APPLICATION_COLUMNS[5], valueCell(totals.representativeCount)),
          cell(APPLICATION_COLUMNS[6], valueCell(totals.performanceCount)),
        ],
      }),
    ],
  });
}

/**
 * Секция «Приезды»/«Отъезды»: общая таблица по всем командам, отсортированная сервером
 * по возрастанию даты и времени. Подсекции отличает одна колонка — «Раннее заселение»
 * есть только у приездов.
 */
function transfersSection(options: {
  title: string;
  columns: string[];
  rows: OrganizerTransferRow[];
  /** Общее число человек по таблице — подвал «Всего человек». */
  peopleTotal: number;
  withEarlyCheckIn: boolean;
}): HTMLElement {
  const { columns, rows, peopleTotal, withEarlyCheckIn } = options;
  const content =
    rows.length === 0
      ? emptyNote(NO_APPLICATIONS)
      : readonlyTable({
          columns,
          rows: rows.map((row) => {
            const cells = [
              cell(columns[0], teamLink(row)),
              cell(columns[1], valueCell(row.city)),
              cell(columns[2], valueCell(row.date)),
              cell(columns[3], valueCell(row.time)),
              cell(columns[4], valueCell(row.peopleCount)),
              cell(columns[5], valueCell(stationName(row.stationId))),
              cell(columns[6], valueCell(row.transferNeeded ? 'Да' : 'Нет')),
              cell(columns[7], valueCell(row.trainOrFlightNumber)),
            ];
            if (withEarlyCheckIn) {
              cells.push(cell(columns[8], valueCell(row.earlyCheckIn ? 'Да' : 'Нет')));
            }
            return cells;
          }),
          footer: peopleFooter(peopleTotal, columns.length),
        });
  return section(options.title, content);
}

/**
 * Подвал таблицы «Приезды»/«Отъезды»: одна ячейка на всю строку — «Всего человек»
 * и сумма по всем строкам таблицы. Ячейка занимает все колонки: итог относится
 * к таблице целиком, а не к одной колонке, поэтому подпись читается целиком и в
 * карточке (узкий экран), и в широкой раскладке.
 */
function peopleFooter(total: number, columnCount: number): HTMLTableSectionElement {
  return el('tfoot', {
    children: [
      el('tr', {
        children: [
          el('td', {
            attrs: { colspan: columnCount },
            text: `${PEOPLE_TOTAL_LABEL}: ${total}`,
          }),
        ],
      }),
    ],
  });
}

/**
 * Секция «Футболки»: размеры по командам и итоговая строка «Всего» — суммы по каждому
 * размеру, которые организатор заказывает у поставщика.
 */
function tshirtsSection(
  rows: OrganizerTshirtRow[],
  totals: OrganizerTshirtTotals,
): HTMLElement {
  const columns = [
    APPLICATION_COLUMNS[0],
    APPLICATION_COLUMNS[1],
    ...TSHIRT_SIZES,
    TSHIRT_TOTAL_COLUMN,
  ];
  const content =
    rows.length === 0
      ? emptyNote(NO_APPLICATIONS)
      : readonlyTable({
          className: 'table--tshirts',
          columns,
          rows: rows.map((row) => [
            cell(columns[0], teamLink(row)),
            cell(columns[1], valueCell(row.city)),
            ...TSHIRT_SIZES.map((size) => cell(size, valueCell(row.sizes[size]))),
            cell(TSHIRT_TOTAL_COLUMN, valueCell(row.total)),
          ]),
          footer: totalsFooter(totals),
        });
  return section('Футболки', content);
}

/**
 * Подвал таблицы «Футболки»: ячейка «Всего» занимает первые две колонки (команда и
 * город) — подпись относится ко всей строке итогов, а не к одной колонке. Последняя
 * ячейка — итог по всем размерам; её подпись в карточке («Итого») отличается от
 * подписи колонки «Всего», чтобы в одной карточке не было двух одинаковых подписей.
 */
function totalsFooter(totals: OrganizerTshirtTotals): HTMLTableSectionElement {
  return el('tfoot', {
    children: [
      el('tr', {
        children: [
          el('td', { attrs: { colspan: 2 }, text: 'Всего' }),
          ...TSHIRT_SIZES.map((size) => cell(size, valueCell(totals.sizes[size]))),
          cell('Итого', valueCell(totals.total)),
        ],
      }),
    ],
  });
}
